import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Pencil, Trash2, BookOpen, ImagePlus, Eye, EyeOff, ExternalLink, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";

const CATEGORIES = ["Wellness", "Mindfulness", "Healing", "Community", "Spirituality", "Nutrition", "Movement", "Stories"];

const EMPTY = { title: "", excerpt: "", body: "", category: "Wellness", cover_image: "", tags: [], published: false };

function ArticleEditor({ article, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...(article || {}) });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((v) => ({ ...v, cover_image: data.url }));
      toast.success("Cover image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "We couldn't upload that file. Please check it and try again."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async (publish) => {
    if (!f.title.trim()) { toast.error("Give your article a title."); return; }
    if (publish && !(f.body || "").replace(/<[^>]+>/g, "").trim()) { toast.error("Write some content before publishing."); return; }
    setSaving(true);
    try {
      const payload = { ...f, published: publish };
      const { data } = article?.id
        ? await api.put(`/enhancer/me/articles/${article.id}`, payload)
        : await api.post("/enhancer/me/articles", payload);
      toast.success(publish ? "Article published! 🎉" : "Draft saved — come back anytime.");
      onSaved(data);
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="eyw-card p-6" data-testid="enh-article-editor">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-xl text-[#1A242B]">{article?.id ? "Edit article" : "New article"}</h3>
        <button onClick={onClose} data-testid="enh-article-cancel" className="text-sm text-[#8FA89B]">← Back to list</button>
      </div>
      <div className="space-y-3">
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Article title"
          data-testid="enh-article-title" className="eyw-input w-full font-serif text-lg" />
        <div className="grid sm:grid-cols-2 gap-3">
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="enh-article-category" className="eyw-input w-full text-sm">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="enh-article-cover-btn"
              className="eyw-btn-ghost !py-2 text-xs disabled:opacity-60">
              {uploading ? <Loader2 className="animate-spin" size={14} /> : <ImagePlus size={14} />} {f.cover_image ? "Change cover" : "Cover image"}
            </button>
            {f.cover_image && <img src={f.cover_image} alt="cover" className="h-9 w-14 object-cover rounded-lg border border-[#E5E0D8]" />}
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadCover} className="hidden" />
          </div>
        </div>
        <textarea value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} rows={2}
          placeholder="Short description (shows in article cards & search results)"
          data-testid="enh-article-excerpt" className="eyw-input w-full text-sm" />
        <RichTextEditor value={f.body} onChange={(body) => setF((v) => ({ ...v, body }))}
          placeholder="Share your wisdom… Use the toolbar for headings, bold, lists, images and links — no code needed."
          testid="enh-article-body" minHeight={320} />
        <div className="flex items-center gap-2 pt-1">
          <button onClick={() => save(false)} disabled={saving} data-testid="enh-article-save-draft" className="eyw-btn-ghost !py-2 text-sm disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={14} /> : null} Save draft
          </button>
          <button onClick={() => save(true)} disabled={saving} data-testid="enh-article-publish" className="eyw-btn !py-2 text-sm disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={14} /> : null} {article?.published ? "Update & keep published" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EnhancerArticlesPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["enh-my-articles"], queryFn: () => api.get("/enhancer/me/articles").then((r) => r.data) });
  const [editing, setEditing] = useState(null); // null | {} | article
  const refresh = () => qc.invalidateQueries({ queryKey: ["enh-my-articles"] });

  const togglePublish = async (a) => {
    try {
      await api.put(`/enhancer/me/articles/${a.id}`, { ...a, published: !a.published });
      toast.success(a.published ? "Article unpublished (kept as draft)" : "Article published! 🎉");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/enhancer/me/articles/${a.id}`);
      toast.success("Article deleted");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (editing !== null) return <ArticleEditor article={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={refresh} />;

  const list = data || [];
  const totalReads = list.reduce((s, a) => s + (a.views || 0), 0);
  return (
    <div data-testid="enh-articles-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><BookOpen className="text-[#144F75]" size={22} /> Articles</h2>
        <div className="flex items-center gap-3">
          {totalReads > 0 && (
            <span className="text-xs text-[#4A5D6B] flex items-center gap-1.5 bg-[#F1EDE6] rounded-full px-3 py-1.5" data-testid="enh-articles-total-reads">
              <BarChart3 size={13} className="text-[#144F75]" /> {totalReads.toLocaleString()} total reads
            </span>
          )}
          <button onClick={() => setEditing({})} data-testid="enh-new-article" className="eyw-btn !py-2 text-sm"><Plus size={16} /> Write an article</button>
        </div>
      </div>
      <p className="text-sm text-[#4A5D6B] mb-5">Write and publish your own articles — published pieces appear on the platform blog and on your public profile.</p>
      {isLoading ? (
        <div className="p-8 text-center"><Loader2 className="animate-spin text-[#144F75] inline" /></div>
      ) : list.length === 0 ? (
        <div className="eyw-card p-8 text-center" data-testid="enh-articles-empty">
          <p className="text-[#4A5D6B]">You haven't written anything yet. Share your first story — your community is listening.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((a) => (
            <div key={a.id} className="eyw-card p-4 flex items-center gap-4" data-testid={`enh-article-${a.id}`}>
              {a.cover_image
                ? <img src={a.cover_image} alt="" className="h-14 w-20 object-cover rounded-lg shrink-0" />
                : <div className="h-14 w-20 rounded-lg bg-[#F1EDE6] shrink-0 flex items-center justify-center"><BookOpen size={18} className="text-[#8FA89B]" /></div>}
              <div className="flex-1 min-w-0">
                <p className="text-[#1A242B] font-medium truncate">{a.title}</p>
                <p className="text-xs text-[#8FA89B]">{a.category} · {(a.created_at || "").slice(0, 10)}
                  {a.published && <span className="text-[#144F75]" data-testid={`enh-article-reads-${a.id}`}> · 👁 {(a.views || 0).toLocaleString()} {a.views === 1 ? "read" : "reads"}</span>}
                </p>
              </div>
              <span className={`text-[11px] rounded-full px-2.5 py-1 shrink-0 ${a.published ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#E0A458]/20 text-[#B5794E]"}`} data-testid={`enh-article-status-${a.id}`}>
                {a.published ? "Published" : "Draft"}
              </span>
              <div className="flex items-center gap-1.5 shrink-0">
                {a.published && (
                  <a href={`/articles/${a.slug || a.id}`} target="_blank" rel="noreferrer" data-testid={`enh-article-view-${a.id}`}
                    className="p-2 rounded-lg hover:bg-[#F1EDE6] text-[#4A5D6B]" title="View live"><ExternalLink size={15} /></a>
                )}
                <button onClick={() => togglePublish(a)} data-testid={`enh-article-toggle-${a.id}`}
                  className="p-2 rounded-lg hover:bg-[#F1EDE6] text-[#4A5D6B]" title={a.published ? "Unpublish" : "Publish"}>
                  {a.published ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                <button onClick={() => setEditing(a)} data-testid={`enh-article-edit-${a.id}`} className="p-2 rounded-lg hover:bg-[#F1EDE6] text-[#144F75]" title="Edit"><Pencil size={15} /></button>
                <button onClick={() => del(a)} data-testid={`enh-article-delete-${a.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
